// Where the backend lives. Set REACT_APP_API_BASE when building for the real
// site (see frontend/.env.example) - Create React App bakes it into the
// build, so it has to be there when `npm run build` runs, not afterwards.
//
// Without it: the local backend during development, and in a production
// build the site's own address - right when the API is served from the same
// domain under /api, and at least never a customer's own localhost.
//
// Exported so the admin client (src/admin/api.js) points at the same backend.
export const API_BASE = (
  process.env.REACT_APP_API_BASE ??
  (process.env.NODE_ENV === 'production' ? '' : 'http://localhost:8000')
).replace(/\/+$/, '');

// FastAPI sends errors two different shapes: our own HTTPException(detail=
// "...") gives a plain string, but a request that fails Pydantic's own
// validation (a missing/malformed field) gives detail as a list of
// {msg, loc, ...} objects instead - new Error(thatArray) stringifies to
// "[object Object]", which is what this exists to avoid.
function errorMessage(body, status) {
  const { detail } = body;
  if (typeof detail === 'string') return detail;
  if (Array.isArray(detail)) {
    return detail.map((e) => e.msg || JSON.stringify(e)).join('; ');
  }
  return `Request failed (${status})`;
}

async function request(path, options = {}) {
  const res = await fetch(`${API_BASE}${path}`, {
    headers: { 'Content-Type': 'application/json' },
    ...options,
  });

  if (!res.ok) {
    const body = await res.json().catch(() => ({}));
    const error = new Error(errorMessage(body, res.status));
    // Callers that care about the difference between kinds of failure read
    // this - the join page treats 409 ("already claimed") as a state to
    // show rather than an error.
    error.status = res.status;
    throw error;
  }

  return res.json();
}

export function getCatalog() {
  return request('/api/catalog');
}

export function previewCoupon(code, subtotalCents) {
  return request('/api/coupons/preview', {
    method: 'POST',
    body: JSON.stringify({ code, subtotal_cents: subtotalCents }),
  });
}

export function createOrder(data) {
  return request('/api/orders', {
    method: 'POST',
    body: JSON.stringify(data),
  });
}

export function createCheckout(publicId, token) {
  return request(`/api/orders/${publicId}/checkout?token=${encodeURIComponent(token)}`, {
    method: 'POST',
  });
}

export function getOrderStatus(publicId, token) {
  return request(`/api/orders/${publicId}?token=${encodeURIComponent(token)}`);
}

export function getJoinInfo(publicId) {
  return request(`/api/join/${publicId}`);
}

export function joinOrder(publicId, data) {
  return request(`/api/join/${publicId}`, {
    method: 'POST',
    body: JSON.stringify(data),
  });
}

export function getPaymentStatus(publicId, token) {
  return request(`/api/orders/${publicId}/payment-status?token=${encodeURIComponent(token)}`);
}